import { useEffect, useMemo, useSyncExternalStore, useState } from 'react';
import type { PropsWithChildren } from 'react';

import {
    keycloak,
    getUserDisplayName,
    getUserEmail,
    isAuthRedirectInProgress,
    runAuthRedirect,
    subscribeToAuthRedirect
} from '@/lib/auth';
import { ensureKeycloakInit, resolveStartupAuthMode } from '@/lib/auth-bootstrap';
import { i18n } from '@/lib/i18n';
import { AuthContext, type AuthContextValue } from '@/providers/AuthProvider.context';

function AuthStartupScreen() {
    return (
        <div className='flex min-h-svh items-center justify-center bg-background px-4 py-12'>
            <div className='text-sm text-muted-foreground'>{i18n.t('common.loading')}</div>
        </div>
    );
}

export function AuthProvider({ children }: PropsWithChildren) {
    const [isReady, setIsReady] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(Boolean(keycloak.authenticated));
    const [token, setToken] = useState<string | null>(keycloak.token ?? null);
    const [error, setError] = useState<unknown | null>(null);
    const authRedirectInProgress = useSyncExternalStore(subscribeToAuthRedirect, isAuthRedirectInProgress, () => false);

    useEffect(() => {
        let mounted = true;

        ensureKeycloakInit(resolveStartupAuthMode(window.location.pathname))
            .then((authenticated) => {
                if (!mounted) return;
                const resolvedToken = keycloak.token ?? null;
                setError(null);
                setIsAuthenticated(Boolean(authenticated || resolvedToken));
                setToken(resolvedToken);
            })
            .catch((resolvedError: unknown) => {
                if (!mounted) return;
                setError(resolvedError);
                setIsAuthenticated(false);
                setToken(null);
            })
            .finally(() => {
                if (mounted) setIsReady(true);
            });

        keycloak.onAuthSuccess = () => {
            if (!mounted) return;
            setError(null);
            setIsAuthenticated(true);
            setToken(keycloak.token ?? null);
        };

        keycloak.onAuthLogout = () => {
            if (!mounted) return;
            setError(null);
            setIsAuthenticated(false);
            setToken(null);
        };

        keycloak.onAuthRefreshSuccess = () => {
            if (!mounted) return;
            setError(null);
            setIsAuthenticated(Boolean(keycloak.authenticated || keycloak.token));
            setToken(keycloak.token ?? null);
        };

        keycloak.onTokenExpired = async () => {
            try {
                await keycloak.updateToken(30);
                if (!mounted) return;
                setError(null);
                setToken(keycloak.token ?? null);
            } catch (resolvedError: unknown) {
                if (!mounted) return;
                setError(resolvedError);
                setIsAuthenticated(false);
                setToken(null);
            }
        };

        return () => {
            mounted = false;
            keycloak.onAuthSuccess = undefined;
            keycloak.onAuthLogout = undefined;
            keycloak.onAuthRefreshSuccess = undefined;
            keycloak.onTokenExpired = undefined;
        };
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({
            isReady,
            isAuthenticated,
            username: isAuthenticated ? getUserDisplayName() : null,
            email: isAuthenticated ? getUserEmail() : null,
            token,
            error,
            login: async (options) => {
                await runAuthRedirect(() =>
                    keycloak.login({ redirectUri: window.location.href, loginHint: options?.loginHint })
                );
            },
            register: async (options) => {
                await runAuthRedirect(() =>
                    keycloak.login({
                        redirectUri: window.location.href,
                        action: 'register',
                        loginHint: options?.loginHint
                    })
                );
            },
            logout: async () => {
                await keycloak.logout({ redirectUri: window.location.origin });
            }
        }),
        [error, isAuthenticated, isReady, token]
    );

    return (
        <AuthContext.Provider value={value}>
            {isReady && !authRedirectInProgress ? children : <AuthStartupScreen />}
        </AuthContext.Provider>
    );
}
